import {
  DashboardState,
  EDatasourceType,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IApiManagement,
  IApiMethods,
  IBluetoothCharacteristic,
  IEvent,
  IScheduledJob,
  IScreenDesignDictionary,
  IWFStep,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { safeParse } from "./designer-utils";

export type DesignerActionContextType =
  | "screen_event"
  | "scheduled_job"
  | "api_method"
  | "workflow_step"
  | "bluetooth_method";

export interface IDesignerActionScope {
  kind: DesignerActionContextType;
  label: string;
  actions: IAction[];
  saveTopLevelAction: (action: IAction, changeReason?: ESdChangeReason) => void;
  deleteTopLevelAction: (action: IAction, changeReason?: ESdChangeReason) => void;
  replaceTopLevelActions: (actions: IAction[], changeReason?: ESdChangeReason) => void;
  kuikaId?: string;
  eventName?: string;
  jobId?: string;
  jobName?: string;
  apiId?: string;
  apiName?: string;
  apiMethodId?: string;
  apiMethodName?: string;
  workflowId?: string;
  workflowName?: string;
  workflowStepId?: string;
  workflowStepName?: string;
  bluetoothMethodId?: string;
  bluetoothMethodName?: string;
  datasourceId?: string;
  datasourceName?: string;
}

import _ from "lodash";

function safeClone<T>(value: T): T {
  return value == null ? value : _.cloneDeep(value);
}

const ensureParsedJson = safeParse;

function normalizeContextType(value: unknown): DesignerActionContextType | null {
  if (!value) return null;

  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  switch (normalized) {
    case "screen":
    case "screen_event":
    case "component_event":
    case "event":
      return "screen_event";
    case "scheduled_job":
    case "scheduled_jobs":
    case "job":
      return "scheduled_job";
    case "api":
    case "api_method":
    case "method":
      return "api_method";
    case "workflow":
    case "workflow_step":
    case "wf_step":
    case "step":
      return "workflow_step";
    case "bluetooth":
    case "bluetooth_method":
    case "ble":
    case "ble_method":
      return "bluetooth_method";
    default:
      return null;
  }
}

/**
 * Input shape for context resolution functions.
 */
export interface IContextInput {
  contextType?: string;
  context?: string;
  kuikaId?: string | number;
  eventName?: string;
  jobId?: string;
  apiId?: string;
  apiMethodId?: string;
  workflowId?: string;
  workflowStepId?: string;
  bluetoothMethodId?: string;
  actionId?: string;
}

/**
 * Helper to access a deeply nested value from an object that may have provider sub-objects.
 * The IScreenDesignContextState is a flattened intersection type, but at runtime the sd object
 * may also carry provider instances with their own `.state` sub-objects. We use this to safely
 * try the provider path first, then fall back to the flat state property.
 */
function providerOr<T>(sd: IScreenDesignContextState, providerKey: string, stateKey: string, fallback: T): T {
  const sdRecord = sd as unknown as Record<string, Record<string, Record<string, unknown>>>;
  const providerValue = sdRecord[providerKey]?.state?.[stateKey];
  if (providerValue !== undefined) return providerValue as T;
  const directValue = (sd as unknown as Record<string, unknown>)[stateKey];
  if (directValue !== undefined) return directValue as T;
  return fallback;
}

function getScheduledJobs(sd: IScreenDesignContextState): IScheduledJob[] {
  return providerOr(sd, "scheduledJobsProvider", "scheduledJobs", sd.scheduledJobs ?? []);
}

function getApis(sd: IScreenDesignContextState): IApiManagement[] {
  return providerOr(sd, "apiManagementProvider", "apiManagements", sd.apiManagements ?? []);
}

function getWorkflows(sd: IScreenDesignContextState): IWorkFlow[] {
  const list = providerOr<IWorkFlow[]>(sd, "workflowProvider", "workFlowList", []);
  return list.length > 0 ? list : ((sd.workFlowList as unknown as IWorkFlow[]) ?? []);
}

interface IDatasourceItem {
  id: string;
  name?: string;
  datasourceType?: EDatasourceType;
}

function getDatasources(sd: IScreenDesignContextState): IDatasourceItem[] {
  return providerOr<IDatasourceItem[]>(
    sd,
    "datasourceProvider",
    "datasources",
    (sd.datasources as unknown as IDatasourceItem[]) ?? []
  );
}

function getBluetoothActionDefinitions(sd: IScreenDesignContextState): IActionDefinitionDto[] {
  return providerOr<IActionDefinitionDto[]>(
    sd,
    "bluetoothMethodProvider",
    "actionDefinitions",
    sd.actionDefinitions ?? []
  );
}

function setContextSelection(sd: IScreenDesignContextState, input: IContextInput): void {
  if (input?.jobId) {
    sd.setActiveScheduledJob?.(input.jobId);
  }

  if (input?.apiId) {
    sd.setActiveApi?.(input.apiId);
  }

  if (input?.apiMethodId) {
    sd.setActiveMethod?.(input.apiMethodId);
  }

  if (input?.workflowStepId) {
    sd.setSelectedWfStep?.(input.workflowStepId);
  }

  if (input?.bluetoothMethodId) {
    const bluetoothAction = getBluetoothActionDefinitions(sd).find(
      (candidate) => String(candidate.id) === String(input.bluetoothMethodId)
    );
    if (bluetoothAction?.kDatasource_Id) {
      const datasource = getDatasources(sd).find(
        (candidate) => String(candidate.id) === String(bluetoothAction.kDatasource_Id)
      );
      if (datasource) {
        sd.setActiveEditingDatasource?.(datasource as Parameters<typeof sd.setActiveEditingDatasource>[0]);
      }
    }
    sd.setActiveBluetoothMethod?.(input.bluetoothMethodId);
  }
}

function resolveScreenEventScope(
  sd: IScreenDesignContextState,
  input: IContextInput
): IDesignerActionScope | { error: string } {
  const { kuikaId, eventName } = input;
  if (!kuikaId) return { error: "kuikaId is required for screen event actions" };
  if (!eventName) return { error: "eventName is required for screen event actions" };

  const dict: IScreenDesignDictionary = providerOr<IScreenDesignDictionary>(
    sd,
    "screenProvider",
    "screenDesignDictionary",
    sd.screenDesignDictionary ?? {}
  );
  const di = dict[parseInt(String(kuikaId), 10)];
  if (!di) return { error: `Component with kuikaId "${kuikaId}" not found` };

  const item = di.item;
  const events: IEvent[] = item?.events || [];
  const evt = events.find((candidate) => candidate.eventName === eventName);
  if (!evt) return { error: `Event "${eventName}" not found on component "${kuikaId}"` };

  if (!Array.isArray(evt.actions)) {
    evt.actions = [];
  }

  return {
    kind: "screen_event",
    label: `${di.friendlyName || item?.name || "Component"} :: ${eventName}`,
    kuikaId: String(kuikaId),
    eventName,
    actions: evt.actions,
    saveTopLevelAction: (action, _changeReason) => {
      sd.saveActionToEvent?.(String(kuikaId), action, evt, _changeReason);
    },
    deleteTopLevelAction: (action) => {
      sd.deleteActionFromEvent?.(String(kuikaId), action, evt);
    },
    replaceTopLevelActions: (actions, changeReason) => {
      evt.actions = safeClone(actions);
      const screenDesign = sd.getScreenDesign?.();
      sd.setScreenDesign?.(screenDesign, changeReason !== undefined ? changeReason : ESdChangeReason.DiActionUpdated);
    }
  };
}

function resolveScheduledJobScope(
  sd: IScreenDesignContextState,
  input: IContextInput
): IDesignerActionScope | { error: string } {
  const jobs = getScheduledJobs(sd);
  const selectedJob = input?.jobId
    ? jobs.find((candidate) => String(candidate.id) === String(input.jobId))
    : sd.getSelectedSj?.();
  const job: IScheduledJob | undefined = selectedJob ? safeClone(selectedJob) : undefined;

  if (!job) {
    return { error: "No scheduled job is active. Open a scheduled job or provide jobId." };
  }

  job.json = (ensureParsedJson(job.json) as IScheduledJob["json"]) || {};
  if (!Array.isArray(job.json?.actions)) {
    if (job.json && typeof job.json === "object") {
      (job.json as Record<string, unknown>).actions = [];
    }
  }
  const jobActions: IAction[] = ((job.json as Record<string, unknown>)?.actions as IAction[]) ?? [];

  return {
    kind: "scheduled_job",
    label: job.name || "Scheduled Job",
    jobId: String(job.id),
    jobName: job.name,
    actions: jobActions,
    saveTopLevelAction: (action) => {
      const nextJob = safeClone(job);
      nextJob.json = (ensureParsedJson(nextJob.json) as IScheduledJob["json"]) || {};
      const nextActions: IAction[] = ((nextJob.json as Record<string, unknown>)?.actions as IAction[]) ?? [];
      const index = nextActions.findIndex((candidate) => candidate.id === action.id);
      if (index >= 0) {
        nextActions[index] = action;
      } else {
        nextActions.push(action);
      }
      (nextJob.json as Record<string, unknown>).actions = nextActions;
      sd.saveScheduledJob?.(nextJob);
    },
    deleteTopLevelAction: (action) => {
      const nextJob = safeClone(job);
      nextJob.json = (ensureParsedJson(nextJob.json) as IScheduledJob["json"]) || {};
      const currentActions = ((nextJob.json as Record<string, unknown>)?.actions as IAction[]) ?? [];
      (nextJob.json as Record<string, unknown>).actions = currentActions.filter(
        (candidate) => candidate.id !== action.id
      );
      sd.saveScheduledJob?.(nextJob);
    },
    replaceTopLevelActions: (actions) => {
      const nextJob = safeClone(job);
      nextJob.json = (ensureParsedJson(nextJob.json) as IScheduledJob["json"]) || {};
      (nextJob.json as Record<string, unknown>).actions = safeClone(actions);
      sd.saveScheduledJob?.(nextJob);
    }
  };
}

function resolveApiMethodScope(
  sd: IScreenDesignContextState,
  input: IContextInput
): IDesignerActionScope | { error: string } {
  const apis = getApis(sd);
  let api: IApiManagement | undefined =
    input?.apiId != null
      ? apis.find((candidate) => String(candidate.id) === String(input.apiId))
      : sd.getSelectedApi?.();
  let method: IApiMethods | undefined = input?.apiMethodId != null ? undefined : sd.getSelectedApiMethod?.();

  if (!api && input?.apiMethodId != null) {
    api = apis.find((candidate) => {
      const json = (typeof candidate.json === "string" ? ensureParsedJson(candidate.json) : candidate.json) as
        | { methods?: IApiMethods[] }
        | undefined;
      return (json?.methods || []).some((methodCandidate) => String(methodCandidate.id) === String(input.apiMethodId));
    });
  }

  if (api && input?.apiMethodId != null) {
    const json = (typeof api.json === "string" ? ensureParsedJson(api.json) : api.json) as
      | { methods?: IApiMethods[] }
      | undefined;
    method = (json?.methods || []).find((candidate) => String(candidate.id) === String(input.apiMethodId));
  }

  if (!api || !method) {
    return { error: "No API method is active. Open an API method or provide apiMethodId." };
  }

  const apiClone = safeClone(api);
  const methodClone = safeClone(method);
  if (!Array.isArray(methodClone.actions)) {
    methodClone.actions = [];
  }

  return {
    kind: "api_method",
    label: `${apiClone.name || "API"} :: ${methodClone.name || "Method"}`,
    apiId: String(apiClone.id),
    apiName: apiClone.name,
    apiMethodId: String(methodClone.id),
    apiMethodName: methodClone.name,
    actions: methodClone.actions!,
    saveTopLevelAction: (action) => {
      const nextMethod = safeClone(methodClone);
      if (!Array.isArray(nextMethod.actions)) {
        nextMethod.actions = [];
      }
      const index = nextMethod.actions.findIndex((candidate) => candidate.id === action.id);
      if (index >= 0) {
        nextMethod.actions[index] = action;
      } else {
        nextMethod.actions.push(action);
      }
      sd.saveApiMethod?.(nextMethod);
    },
    deleteTopLevelAction: (action) => {
      const nextMethod = safeClone(methodClone);
      nextMethod.actions = (nextMethod.actions || []).filter((candidate) => candidate.id !== action.id);
      sd.saveApiMethod?.(nextMethod);
    },
    replaceTopLevelActions: (actions) => {
      const nextMethod = safeClone(methodClone);
      nextMethod.actions = safeClone(actions);
      sd.saveApiMethod?.(nextMethod);
    }
  };
}

function resolveWorkflowStepScope(
  sd: IScreenDesignContextState,
  input: IContextInput
): IDesignerActionScope | { error: string } {
  const workflows = getWorkflows(sd);
  const workflow: IWorkFlow | undefined =
    input?.workflowId != null
      ? workflows.find((candidate) => String(candidate.id) === String(input.workflowId))
      : sd.selectedWf;

  if (!workflow) {
    return { error: "No workflow is active. Open a workflow first." };
  }

  const step: IWFStep | undefined =
    input?.workflowStepId != null
      ? sd.findStepRecursively?.(workflow, (candidate) => String(candidate.id) === String(input.workflowStepId))
      : sd.getSelectedWfStep?.();

  if (!step) {
    return { error: "No workflow step is active. Select a workflow step or provide workflowStepId." };
  }

  const workflowClone = safeClone(workflow);
  const stepClone = safeClone(step);
  if (!Array.isArray(stepClone.actions)) {
    stepClone.actions = [];
  }

  return {
    kind: "workflow_step",
    label: `${workflowClone.name || "Workflow"} :: ${stepClone.name || stepClone.id}`,
    workflowId: String(workflowClone.id),
    workflowName: workflowClone.name,
    workflowStepId: String(stepClone.id),
    workflowStepName: stepClone.name,
    actions: stepClone.actions!,
    saveTopLevelAction: (action, _changeReason) => {
      const nextStep = safeClone(stepClone);
      if (!Array.isArray(nextStep.actions)) {
        nextStep.actions = [];
      }
      const index = nextStep.actions.findIndex((candidate) => candidate.id === action.id);
      if (index >= 0) {
        nextStep.actions[index] = action;
      } else {
        nextStep.actions.push(action);
      }
      sd.updateWfStep?.(nextStep, workflowClone);
    },
    deleteTopLevelAction: (action, _changeReason) => {
      const nextStep = safeClone(stepClone);
      nextStep.actions = (nextStep.actions || []).filter((candidate) => candidate.id !== action.id);
      sd.updateWfStep?.(nextStep, workflowClone);
    },
    replaceTopLevelActions: (actions) => {
      const nextStep = safeClone(stepClone);
      nextStep.actions = safeClone(actions);
      sd.updateWfStep?.(nextStep, workflowClone);
    }
  };
}

function resolveBluetoothMethodScope(
  sd: IScreenDesignContextState,
  input: IContextInput
): IDesignerActionScope | { error: string } {
  let method: IActionDefinitionDto | undefined =
    input?.bluetoothMethodId != null
      ? getBluetoothActionDefinitions(sd).find((candidate) => String(candidate.id) === String(input.bluetoothMethodId))
      : sd.getSelectedBluetoothMethod?.();

  if (!method) {
    return { error: "No Bluetooth method is active. Select a Bluetooth method or provide bluetoothMethodId." };
  }

  method = safeClone(method);
  const parsedInfo = ensureParsedJson(method.bluetoothInfo) as IBluetoothCharacteristic | null;
  const bluetoothInfo: IBluetoothCharacteristic =
    parsedInfo ?? ({ actions: [] } as unknown as IBluetoothCharacteristic);
  if (!Array.isArray(bluetoothInfo.actions)) {
    bluetoothInfo.actions = [];
  }
  method.bluetoothInfo = bluetoothInfo;

  const datasource = getDatasources(sd).find((candidate) => String(candidate.id) === String(method!.kDatasource_Id));

  return {
    kind: "bluetooth_method",
    label: `${datasource?.name || "Bluetooth Datasource"} :: ${method.name || "Bluetooth Method"}`,
    bluetoothMethodId: String(method.id),
    bluetoothMethodName: method.name,
    datasourceId: method.kDatasource_Id != null ? String(method.kDatasource_Id) : undefined,
    datasourceName: datasource?.name,
    actions: bluetoothInfo.actions,
    saveTopLevelAction: (action) => {
      const nextMethod = safeClone(method!);
      const nextParsed = ensureParsedJson(nextMethod.bluetoothInfo) as IBluetoothCharacteristic | null;
      const nextInfo: IBluetoothCharacteristic = nextParsed ?? ({ actions: [] } as unknown as IBluetoothCharacteristic);
      if (!Array.isArray(nextInfo.actions)) {
        nextInfo.actions = [];
      }
      const index = nextInfo.actions.findIndex((candidate) => candidate.id === action.id);
      if (index >= 0) {
        nextInfo.actions[index] = action;
      } else {
        nextInfo.actions.push(action);
      }
      nextMethod.bluetoothInfo = nextInfo;
      sd.updateSelectedBluetoothMethodActions?.(nextInfo.actions);
    },
    deleteTopLevelAction: (action) => {
      const nextMethod = safeClone(method!);
      const nextParsed = ensureParsedJson(nextMethod.bluetoothInfo) as IBluetoothCharacteristic | null;
      const nextInfo: IBluetoothCharacteristic = nextParsed ?? ({ actions: [] } as unknown as IBluetoothCharacteristic);
      nextInfo.actions = (nextInfo.actions || []).filter((candidate) => candidate.id !== action.id);
      nextMethod.bluetoothInfo = nextInfo;
      sd.updateSelectedBluetoothMethodActions?.(nextInfo.actions);
    },
    replaceTopLevelActions: (actions) => {
      const nextMethod = safeClone(method!);
      const nextParsed = ensureParsedJson(nextMethod.bluetoothInfo) as IBluetoothCharacteristic | null;
      const nextInfo: IBluetoothCharacteristic = nextParsed ?? ({ actions: [] } as unknown as IBluetoothCharacteristic);
      nextInfo.actions = safeClone(actions);
      nextMethod.bluetoothInfo = nextInfo;
      sd.updateSelectedBluetoothMethodActions?.(safeClone(actions));
    }
  };
}

export function getDesignerNestedActionCollections(
  action: IAction
): Array<{ collectionType: string; actions: IAction[]; parentActionId: string; branch?: string }> {
  const collections: Array<{ collectionType: string; actions: IAction[]; parentActionId: string; branch?: string }> =
    [];

  if (Array.isArray(action?.ifThenElseData?.ifActions)) {
    collections.push({
      collectionType: "if",
      branch: "if",
      actions: action.ifThenElseData!.ifActions,
      parentActionId: action.id
    });
  }

  if (Array.isArray(action?.ifThenElseData?.elseActions)) {
    collections.push({
      collectionType: "else",
      branch: "else",
      actions: action.ifThenElseData!.elseActions,
      parentActionId: action.id
    });
  }

  if (Array.isArray(action?.forEachData?.actions)) {
    collections.push({
      collectionType: "forEach",
      actions: action.forEachData!.actions,
      parentActionId: action.id
    });
  }

  if (Array.isArray(action?.recurringActionsData?.recurringActions)) {
    collections.push({
      collectionType: "recurring",
      actions: action.recurringActionsData!.recurringActions,
      parentActionId: action.id
    });
  }

  if (Array.isArray(action?.mqttSubscribeActionData?.mqttSubscribeActions)) {
    collections.push({
      collectionType: "mqttSubscribe",
      actions: action.mqttSubscribeActionData!.mqttSubscribeActions,
      parentActionId: action.id
    });
  }

  return collections;
}

export function findDesignerActionInTree(
  actions: IAction[],
  actionId: string,
  rootAction?: IAction
): { action: IAction; rootAction: IAction } | null {
  for (const action of actions || []) {
    const resolvedRootAction = rootAction || action;
    if (action.id === actionId) {
      return { action, rootAction: resolvedRootAction };
    }

    for (const nested of getDesignerNestedActionCollections(action)) {
      const match = findDesignerActionInTree(nested.actions, actionId, resolvedRootAction);
      if (match) {
        return match;
      }
    }
  }

  return null;
}

export function replaceDesignerActionInTree(
  rootAction: IAction,
  targetActionId: string,
  replacementAction: IAction
): IAction {
  const nextRootAction = safeClone(rootAction);

  function replace(actions: IAction[]): boolean {
    const index = actions.findIndex((candidate) => candidate.id === targetActionId);
    if (index >= 0) {
      actions[index] = replacementAction;
      return true;
    }

    for (const action of actions) {
      for (const nested of getDesignerNestedActionCollections(action)) {
        if (replace(nested.actions)) {
          return true;
        }
      }
    }

    return false;
  }

  replace([nextRootAction]);
  return nextRootAction;
}

export function deleteDesignerActionFromTree(rootAction: IAction, targetActionId: string): IAction {
  const nextRootAction = safeClone(rootAction);

  function remove(actions: IAction[]): boolean {
    const index = actions.findIndex((candidate) => candidate.id === targetActionId);
    if (index >= 0) {
      actions.splice(index, 1);
      return true;
    }

    for (const action of actions) {
      for (const nested of getDesignerNestedActionCollections(action)) {
        if (remove(nested.actions)) {
          return true;
        }
      }
    }

    return false;
  }

  remove([nextRootAction]);
  return nextRootAction;
}

interface IActionSummary {
  actionId: string;
  label: string;
  actionDefinitionId: string | undefined;
  parentActionId: string | null;
  collectionType: string;
  branch: string | null;
  depth: number;
  contextType: DesignerActionContextType;
  kuikaId: string | undefined;
  eventName: string | undefined;
}

export function collectDesignerActionsFromCollection(
  actions: IAction[],
  scope: IDesignerActionScope
): IActionSummary[] {
  const results: IActionSummary[] = [];

  const walk = (
    currentActions: IAction[],
    options: { parentActionId?: string; collectionType?: string; branch?: string; depth: number }
  ) => {
    for (const action of currentActions || []) {
      results.push({
        actionId: action.id,
        label: action.label || action.description || "Unnamed",
        actionDefinitionId: action.actionDefinitionId || undefined,
        parentActionId: options.parentActionId || null,
        collectionType: options.collectionType || "topLevel",
        branch: options.branch || null,
        depth: options.depth,
        contextType: scope.kind,
        kuikaId: scope.kuikaId,
        eventName: scope.eventName
      });

      for (const nested of getDesignerNestedActionCollections(action)) {
        walk(nested.actions, {
          parentActionId: action.id,
          collectionType: nested.collectionType,
          branch: nested.branch,
          depth: options.depth + 1
        });
      }
    }
  };

  walk(actions || [], { depth: 0 });
  return results;
}

export function resolveDesignerActionScope(
  sdInstance: ScreenDesignInstance,
  input: IContextInput = {}
): IDesignerActionScope | { error: string } {
  const sd = getValues(sdInstance);
  setContextSelection(sd, input);

  const explicitContext = normalizeContextType(input.contextType ?? input.context);
  if (input.kuikaId || input.eventName || explicitContext === "screen_event") {
    return resolveScreenEventScope(sd, input);
  }

  if (explicitContext === "scheduled_job") {
    return resolveScheduledJobScope(sd, input);
  }

  if (explicitContext === "api_method") {
    return resolveApiMethodScope(sd, input);
  }

  if (explicitContext === "workflow_step") {
    return resolveWorkflowStepScope(sd, input);
  }

  if (explicitContext === "bluetooth_method") {
    return resolveBluetoothMethodScope(sd, input);
  }

  const dashboardState = sd.dashboardState;

  if (dashboardState === DashboardState.scheduledJobs && sd.getSelectedSj?.()) {
    return resolveScheduledJobScope(sd, input);
  }

  if (dashboardState === DashboardState.apiManagement && sd.getSelectedApiMethod?.()) {
    return resolveApiMethodScope(sd, input);
  }

  if (dashboardState === DashboardState.workFlow && sd.selectedWf && sd.getSelectedWfStep?.()) {
    return resolveWorkflowStepScope(sd, input);
  }

  if (
    dashboardState === DashboardState.dataDiagramScheme &&
    sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService &&
    sd.getSelectedBluetoothMethod?.()
  ) {
    return resolveBluetoothMethodScope(sd, input);
  }

  return {
    error: "No action context is active. Provide kuikaId/eventName or navigate/select a current action context first."
  };
}

export function findDesignerActionInCurrentContext(
  sdInstance: ScreenDesignInstance,
  input: IContextInput
):
  | { sd: IScreenDesignContextState; scope: IDesignerActionScope; action: IAction; rootAction: IAction }
  | { error: string } {
  const { actionId } = input || {};
  if (!actionId) return { error: "actionId is required" };

  const sd = getValues(sdInstance);
  const scope = resolveDesignerActionScope(sdInstance, input);
  if ("error" in scope) return scope;

  const located = findDesignerActionInTree(scope.actions || [], actionId);
  if (!located) {
    return { error: `Action "${actionId}" not found in ${scope.kind}` };
  }

  return {
    sd,
    scope,
    action: located.action,
    rootAction: located.rootAction
  };
}
